import React, { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/utils/i18n";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import Dropdown from "@/components/ui/dropdown";
import {
  Actions as ActionsIcon,
  ArrowRight,
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
  ArrowsSort as ArrowsSortIcon,
} from "@/components/icons";
import { renderPaginationNumbers } from "@/utils/pagination";
import { Pdf, DocsFile, Document } from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type {
  LibraryFolderItem,
  LibraryFileItem,
} from "../types/library.types";
import { LibraryShareType } from "../types/library.types";
import { libraryApi, libraryScopeFromBasePath } from "../services/library.api";
import FolderCard from "../components/FolderCard";
import AddFolderModal from "../components/AddFolderModal";
import AddDocumentModal from "../components/AddDocumentModal";
import DocumentViewerModal from "../components/DocumentViewerModal";
import ConfirmModal from "@/components/ui/confirm-modal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./LibraryPage.module.scss";

const FILE_ICON_MAP: Record<
  string,
  React.ComponentType<{ width?: number; height?: number }>
> = {
  pdf: Pdf,
  doc: DocsFile,
  xls: Document,
  txt: Document,
  ppt: Document,
  png: Document,
};

function getFileIcon(fileType: string) {
  const Icon = FILE_ICON_MAP[fileType.toLowerCase()] ?? Document;
  return <Icon width={20} height={20} />;
}

const LIBRARY_SORT_OPTIONS = [
  { value: "name_asc", label: i18n.t("librarySort.nameAsc") },
  { value: "name_desc", label: i18n.t("librarySort.nameDesc") },
  { value: "createdAt_desc", label: i18n.t("librarySort.createdAtDesc") },
  { value: "createdAt_asc", label: i18n.t("librarySort.createdAtAsc") },
  { value: "updatedAt_desc", label: i18n.t("librarySort.updatedAtDesc") },
  { value: "updatedAt_asc", label: i18n.t("librarySort.updatedAtAsc") },
];

export type LibraryBasePath = "library" | "admin-library";

interface LibraryPageProps {
  basePath?: LibraryBasePath;
}

const LibraryPage: React.FC<LibraryPageProps> = ({
  basePath = "admin-library",
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.LIBRARY_CREATE);
  const prefix = `/dashboard/${basePath}`;
  const canDelete = hasPermission(PERMISSIONS.LIBRARY_DELETE);
  const [folders, setFolders] = useState<LibraryFolderItem[]>([]);
  const [documents, setDocuments] = useState<LibraryFileItem[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const SEARCH_DEBOUNCE_MS = 300;
  const [dateFilter, setDateFilter] = useState<string | null>(null);
  const [folderPage, setFolderPage] = useState(1);
  const [folderPageSize, setFolderPageSize] = useState(8);
  const [totalFolders, setTotalFolders] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalDocuments, setTotalDocuments] = useState(0);
  const [folderPageSizeLabel, setFolderPageSizeLabel] = useState("8");
  const [sort, setSort] = useState("updatedAt_desc");
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [addFolderModalOpen, setAddFolderModalOpen] = useState(false);
  const [addFolderModalMode, setAddFolderModalMode] = useState<
    "create" | "add"
  >("create");
  const [addDocumentModalOpen, setAddDocumentModalOpen] = useState(false);
  const [viewerDocument, setViewerDocument] = useState<{
    url: string;
    name: string;
  } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryFileItem | null>(
    null,
  );
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteFolderTarget, setDeleteFolderTarget] =
    useState<LibraryFolderItem | null>(null);
  const [deleteFolderLoading, setDeleteFolderLoading] = useState(false);

  const fetchFolders = useCallback(async () => {
    setFoldersLoading(true);
    try {
      const [sortBy, sortOrder] = sort.split("_") as [
        "name" | "createdAt" | "updatedAt",
        "asc" | "desc",
      ];
      const res = await libraryApi.getItems({
        scope: libraryScopeFromBasePath(basePath),
        type: "folder",
        page: folderPage,
        limit: folderPageSize,
        search: searchTerm || undefined,
        updatedAfter: dateFilter || undefined,
        sortBy,
        sortOrder,
      });
      const result = res as { data: LibraryFolderItem[]; total: number };
      setFolders(result.data);
      setTotalFolders(result.total);
    } finally {
      setFoldersLoading(false);
    }
  }, [basePath, folderPage, folderPageSize, searchTerm, dateFilter, sort]);

  const fetchDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const [sortBy, sortOrder] = sort.split("_") as [
        "name" | "createdAt" | "updatedAt",
        "asc" | "desc",
      ];
      const res = await libraryApi.getItems({
        scope: libraryScopeFromBasePath(basePath),
        type: "file",
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
        updatedAfter: dateFilter || undefined,
        sortBy,
        sortOrder,
      });
      const result = res as { data: LibraryFileItem[]; total: number };
      setDocuments(result.data);
      setTotalDocuments(result.total);
    } finally {
      setDocumentsLoading(false);
    }
  }, [basePath, currentPage, pageSize, searchTerm, dateFilter, sort]);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchTerm(searchInputValue);
      setFolderPage(1);
      setCurrentPage(1);
      searchDebounceRef.current = null;
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInputValue]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node))
        setSortOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (value: string) => setSearchInputValue(value);
  const handleFilterChange = (filters: Record<string, unknown>) => {
    const dateVal = filters.updatedDate;
    setDateFilter(
      dateVal instanceof Date
        ? dateVal.toISOString()
        : (dateVal as string) || null,
    );
  };

  const folderTotalPages =
    totalFolders && folderPageSize
      ? Math.ceil(totalFolders / folderPageSize)
      : 1;
  const handleFolderPageSizeChange = (value: string | string[]) => {
    const size = Number(Array.isArray(value) ? value[0] : value);
    setFolderPageSize(size);
    setFolderPageSizeLabel(String(size));
    setFolderPage(1);
  };

  const openAddFolderModal = (mode: "create" | "add") => {
    setAddFolderModalMode(mode);
    setAddFolderModalOpen(true);
  };
  const handleAddFolderModalClose = () => setAddFolderModalOpen(false);
  const handleAddFolderSubmit = async (payload: {
    folderName: string;
    share: "everyone" | "admins" | "custom";
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
    uploadedFileIds?: string[];
    files?: File[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      const { id: folderId } = await libraryApi.createFolder({
        name: payload.folderName,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      const hasFiles =
        (payload.uploadedFileIds?.length ?? 0) > 0 ||
        (payload.files?.length ?? 0) > 0;
      if (hasFiles) {
        await libraryApi.uploadDocuments({
          fileIds: payload.uploadedFileIds,
          files: payload.files,
          parentItemId: folderId,
          shareType,
          sharedUserIds:
            payload.share === "custom" ? payload.sharedUserIds : undefined,
          sharedDepartmentIds:
            payload.share === "custom"
              ? payload.sharedDepartmentIds
              : undefined,
        });
        toast.success(
          t("library.folder.createdWithFiles"),
        );
      } else {
        toast.success(t("library.folder.created"));
      }
      handleAddFolderModalClose();
      fetchFolders();
      if (hasFiles) fetchDocuments();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleAddDocumentModalClose = () => setAddDocumentModalOpen(false);
  const handleAddDocumentSubmit = async (payload: {
    share: "everyone" | "admins" | "custom";
    uploadedFileIds?: string[];
    files?: File[];
    sharedUserIds?: string[];
    sharedDepartmentIds?: string[];
  }) => {
    const shareType =
      payload.share === "everyone"
        ? LibraryShareType.EVERYONE
        : payload.share === "admins"
          ? LibraryShareType.ADMINS
          : LibraryShareType.PRIVATE;
    try {
      await libraryApi.uploadDocuments({
        fileIds: payload.uploadedFileIds?.length
          ? payload.uploadedFileIds
          : undefined,
        files: payload.uploadedFileIds?.length ? undefined : payload.files,
        shareType,
        sharedUserIds:
          payload.share === "custom" ? payload.sharedUserIds : undefined,
        sharedDepartmentIds:
          payload.share === "custom" ? payload.sharedDepartmentIds : undefined,
      });
      toast.success(t("library.document.uploaded"));
      handleAddDocumentModalClose();
      fetchDocuments();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    }
  };

  const handleFolderMenuAction = (
    action: string,
    folder: LibraryFolderItem,
  ) => {
    if (action === "open") {
      navigate(`${prefix}/folders/${folder.id}`, {
        state: { folderName: folder.name },
      });
    } else if (action === "delete" && basePath !== "library") {
      setDeleteFolderTarget(folder);
    }
  };

  const handleFolderOpen = (folder: LibraryFolderItem) => {
    navigate(`${prefix}/folders/${folder.id}`, {
      state: { folderName: folder.name },
    });
  };

  const handleDropdownToggle = (id: string, open: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [id]: open }));
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await libraryApi.deleteItem(deleteTarget.id);
      toast.success(t("library.deleteModal.deleted"));
      setDeleteTarget(null);
      fetchDocuments();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteFolderConfirm = async () => {
    if (!deleteFolderTarget) return;
    setDeleteFolderLoading(true);
    try {
      await libraryApi.deleteItem(deleteFolderTarget.id);
      toast.success(t("library.folder.deleted"));
      setDeleteFolderTarget(null);
      fetchFolders();
      fetchDocuments();
    } catch {
      // Hata toast'ı apiClient interceptor'da gösteriliyor
    } finally {
      setDeleteFolderLoading(false);
    }
  };

  const dateFilterField = {
    name: "updatedDate",
    label: t("library.filters.updatedDate"),
    type: "date" as const,
    placeholder: t("library.filters.updatedDatePlaceholder"),
  };

  const documentColumns: TableColumn<LibraryFileItem>[] = [
    {
      header: t("library.documents.cols.name"),
      accessor: (row) => (
        <div className={styles.documentNameCell}>
          <span className={styles.fileIconWrap}>
            {getFileIcon(row.fileType)}
          </span>
          <span>{row.name}</span>
        </div>
      ),
    },
    {
      header: t("library.documents.cols.uploadedBy"),
      accessor: (row) => row.uploadedBy,
    },
    {
      header: t("library.documents.cols.lastUpdated"),
      accessor: (row) => row.lastUpdatedFormatted,
    },
    {
      header: t("library.documents.cols.fileSize"),
      accessor: (row) => row.fileSize,
    },
    {
      header: t("library.documents.cols.sharedWith"),
      accessor: (row) => (
        <div className={styles.sharedWith}>
          <div className={styles.avatarStack}>
            {[1, 2, 3].map((i) => (
              <div key={i} className={styles.avatarCircle} title={`User ${i}`}>
                {i}
              </div>
            ))}
          </div>
          <span className={styles.plusBadge}>
            {row.sharedWithCount != null ? `+${row.sharedWithCount}` : "—"}
          </span>
        </div>
      ),
    },
    {
      header: t("library.documents.cols.action"),
      accessor: (row) => {
        const isOpen = openDropdowns[row.id] ?? false;
        return (
          <Popover
            content={
              <div className={styles.menuDropdown}>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (!row.downloadUrl) {
                      toast.error(
                        t("library.download.noUrl"),
                      );
                      return;
                    }
                    setViewerDocument({ url: row.downloadUrl, name: row.name });
                  }}
                >
                  {t("common.view")}
                </button>
                <button
                  type="button"
                  className={styles.menuItem}
                  onClick={() => {
                    if (!row.downloadUrl) {
                      toast.error(
                        t("library.download.noUrl"),
                      );
                      return;
                    }
                    window.open(
                      row.downloadUrl,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  {t("common.download")}
                </button>
                {canDelete && basePath !== "library" && (
                  <button
                    type="button"
                    className={styles.menuItem}
                    onClick={() => {
                      handleDropdownToggle(row.id, false);
                      setDeleteTarget(row);
                    }}
                  >
                    {t("common.delete")}
                  </button>
                )}
              </div>
            }
            isOpen={isOpen}
            onClose={() => handleDropdownToggle(row.id, false)}
            position="bottom"
          >
            <button
              type="button"
              className={styles.actionButton}
              onClick={(e) => {
                e.stopPropagation();
                handleDropdownToggle(row.id, !isOpen);
              }}
            >
              <ActionsIcon width={20} height={20} />
            </button>
          </Popover>
        );
      },
    },
  ];

  return (
    <DashboardLayout>
      <Header title={t("library.title")} />

      <div className={styles.pageContainer}>
        <PageHeader
          searchPlaceholder={t("library.searchPlaceholder")}
          searchValue={searchInputValue}
          onSearch={handleSearch}
          filters={[dateFilterField]}
          onFilterChange={handleFilterChange}
          leftExtraContent={
            <div className={styles.headerSortGroup}>
              <div className={styles.sectionSortWrap} ref={sortRef}>
                <button
                  type="button"
                  className={`${styles.sortBtn} ${sortOpen ? styles.sortBtnActive : ""} ${sort ? styles.sortBtnHasSelection : ""}`}
                  onClick={() => setSortOpen((v) => !v)}
                  aria-label={t("library.sort.title")}
                >
                  <ArrowsSortIcon width={20} height={20} />
                </button>
                {sortOpen && (
                  <div className={styles.sortDropdown}>
                    {LIBRARY_SORT_OPTIONS.map((opt) => (
                      <div
                        key={opt.value}
                        role="button"
                        tabIndex={0}
                        className={`${styles.sortOption} ${sort === opt.value ? styles.sortOptionSelected : ""}`}
                        onClick={() => {
                          setSort(opt.value);
                          setFolderPage(1);
                          setCurrentPage(1);
                          setSortOpen(false);
                        }}
                        onKeyDown={(e) =>
                          e.key === "Enter" &&
                          (setSort(opt.value),
                          setFolderPage(1),
                          setCurrentPage(1),
                          setSortOpen(false))
                        }
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          }
          addButtonLabel={
            canCreate && basePath !== "library"
              ? t("library.addButton")
              : undefined
          }
          addMenuOptions={
            canCreate && basePath !== "library"
              ? [
                  {
                    label: t("library.addMenu.createFolder"),
                    onClick: () => openAddFolderModal("create"),
                  },
                  {
                    label: t("library.addMenu.addFolder"),
                    onClick: () => openAddFolderModal("add"),
                  },
                  {
                    label: t("library.addMenu.addDocument"),
                    onClick: () => setAddDocumentModalOpen(true),
                  },
                ]
              : undefined
          }
        />

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {t("library.folders.title")}
            </h2>
            <button
              type="button"
              className={styles.sectionLink}
              aria-label={t("library.folders.viewAll")}
              onClick={() => navigate(`${prefix}/folders`)}
            >
              <ArrowRight width={20} height={20} />
            </button>
          </div>
          {foldersLoading ? (
            <div className={styles.foldersLoading}>
              {t("common.loading")}
            </div>
          ) : folders.length === 0 ? (
            <div className={styles.foldersEmpty}>
              {t("library.folders.noData")}
            </div>
          ) : (
            <>
              <div className={styles.foldersGrid}>
                {folders.map((folder) => (
                  <FolderCard
                    key={folder.id}
                    folder={folder}
                    onMenuAction={handleFolderMenuAction}
                    onOpen={handleFolderOpen}
                    readOnly={basePath === "library"}
                    canDelete={canDelete && basePath !== "library"}
                  />
                ))}
              </div>
              {totalFolders > 0 && folderPageSize > 0 && (
                <div className={styles.foldersPagination}>
                  <div className={styles.paginationLeft}>
                    <span className={styles.paginationLabel}>
                      {t("pagination.show")}
                    </span>
                    <Dropdown
                      onSelect={handleFolderPageSizeChange}
                      options={[8, 12, 16, 24].map((size) => ({
                        label: String(size),
                        value: String(size),
                      }))}
                      menuPosition="top"
                      selectedLabel={folderPageSizeLabel}
                      value={folderPageSizeLabel}
                    />
                    <span className={styles.paginationDetails}>
                      {t("pagination.details", {
                        total: totalFolders,
                        start: folderPage * folderPageSize - folderPageSize + 1,
                        end: Math.min(
                          folderPage * folderPageSize,
                          totalFolders,
                        ),
                      })}
                    </span>
                  </div>
                  <div className={styles.paginationControls}>
                    <button
                      type="button"
                      className={styles.paginationArrow}
                      disabled={folderPage === 1}
                      onClick={() => setFolderPage((p) => p - 1)}
                      aria-label={t("pagination.previous")}
                    >
                      <ArrowLeftIcon height={16} width={16} />
                    </button>
                    {renderPaginationNumbers(
                      folderPage,
                      folderTotalPages,
                      setFolderPage,
                    )}
                    <button
                      type="button"
                      className={styles.paginationArrow}
                      disabled={folderPage >= folderTotalPages}
                      onClick={() => setFolderPage((p) => p + 1)}
                      aria-label={t("pagination.next")}
                    >
                      <ArrowRightIcon height={16} width={16} />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>

        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>
              {t("library.documents.title")}
            </h2>
            <button
              type="button"
              className={styles.sectionLink}
              aria-label={t("library.documents.viewAll")}
              onClick={() => navigate(`${prefix}/documents`)}
            >
              <ArrowRight width={20} height={20} />
            </button>
          </div>
          <div className={styles.documentsTableWrap}>
            <Table
              columns={documentColumns}
              data={documents}
              isLoading={documentsLoading}
              getRowId={(row) => row.id}
              totalRecords={totalDocuments}
              pageSize={pageSize}
              currentPage={currentPage}
              onPageChange={(p) => setCurrentPage(p)}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setCurrentPage(1);
              }}
              renderActions={() => null}
              hasVisibleActions={false}
              openDropdowns={openDropdowns}
              contentBasedHeight
              emptyMessage={t("library.documents.noData")}
            />
          </div>
        </section>

        <AddFolderModal
          isOpen={addFolderModalOpen}
          onClose={handleAddFolderModalClose}
          mode={addFolderModalMode}
          onSubmit={handleAddFolderSubmit}
        />

        <AddDocumentModal
          isOpen={addDocumentModalOpen}
          onClose={handleAddDocumentModalClose}
          onSubmit={handleAddDocumentSubmit}
        />

        <DocumentViewerModal
          isOpen={!!viewerDocument}
          onClose={() => setViewerDocument(null)}
          downloadUrl={viewerDocument?.url ?? null}
          title={viewerDocument?.name}
        />

        <ConfirmModal
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title={t("library.deleteModal.title")}
          message={t("library.deleteModal.message")}
          confirmText={t("common.delete")}
          cancelText={t("buttons.cancel")}
          loading={deleteLoading}
          confirmVariant="danger"
        />

        <ConfirmModal
          isOpen={!!deleteFolderTarget}
          onClose={() => setDeleteFolderTarget(null)}
          onConfirm={handleDeleteFolderConfirm}
          title={t("library.folder.deleteModal.title")}
          message={t("library.folder.deleteModal.message")}
          confirmText={t("common.delete")}
          cancelText={t("buttons.cancel")}
          loading={deleteFolderLoading}
          confirmVariant="danger"
        />
      </div>
    </DashboardLayout>
  );
};

export default LibraryPage;
